import styled from '@emotion/styled';
import React, { useCallback, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ApprovalRequestStatus } from '../../../../__generated__/graphql';
import { useUpdateApprovalRequestStatusBulk } from '../GoalSidebar/useObjectiveMutations';
import { useToasts } from '../../../../../../context/Toast';
import { useGoalSelection } from './GoalSelectionContext';
import { Theme } from '../../../../../../theme';
import Button from '../../../../../../components/button';

const i18p = 'performanceReview.goals2.approvalStatus.';

export const BulkApprovalBar: React.FC = () => {
  const { t } = useTranslation();
  const { selectedGoalIds, getSelectableGoals, deselectAll } =
    useGoalSelection();
  const { addSuccess, addError } = useToasts();
  const [updateStatusBulk] = useUpdateApprovalRequestStatusBulk();
  const [loadingAction, setLoadingAction] = useState<
    'approve' | 'reject' | null
  >(null);
  const [showRejectComment, setShowRejectComment] = useState(false);
  const [comment, setComment] = useState('');

  const getGoalsWithApprovalRequest = useCallback(() => {
    return getSelectableGoals().filter(
      (g) => selectedGoalIds.has(g.id) && g.approvalRequestId != null,
    );
  }, [getSelectableGoals, selectedGoalIds]);

  const handleBulkAction = useCallback(
    async (
      action: 'approve' | 'reject',
      status: ApprovalRequestStatus,
      actionComment: string | null,
    ) => {
      const goals = getGoalsWithApprovalRequest();
      const ids = goals.map((g) => g.approvalRequestId!);
      setLoadingAction(action);

      try {
        await updateStatusBulk({
          variables: {
            ids,
            status,
            comment: actionComment,
          },
        });
        const messageKey =
          status === ApprovalRequestStatus.Approved
            ? 'bulkApproved'
            : 'bulkRejected';
        addSuccess(t(i18p + messageKey, { count: goals.length }));
        deselectAll();
        setShowRejectComment(false);
        setComment('');
      } catch (e) {
        addError(
          t('performanceReview.goals2.bulkApproval.failedFor', {
            names: goals.map((g) => g.name).join(', '),
          }),
        );
      }

      setLoadingAction(null);
    },
    [
      getGoalsWithApprovalRequest,
      updateStatusBulk,
      addSuccess,
      addError,
      t,
      deselectAll,
    ],
  );

  const handleApprove = useCallback(() => {
    handleBulkAction('approve', ApprovalRequestStatus.Approved, null);
  }, [handleBulkAction]);

  const handleReject = useCallback(() => {
    if (!showRejectComment) {
      setShowRejectComment(true);
      return;
    }
    handleBulkAction('reject', ApprovalRequestStatus.Rejected, comment || null);
  }, [showRejectComment, comment, handleBulkAction]);

  const handleCancel = useCallback(() => {
    setShowRejectComment(false);
    setComment('');
    deselectAll();
  }, [deselectAll]);

  const approvableCount = getGoalsWithApprovalRequest().length;

  if (selectedGoalIds.size === 0 || approvableCount === 0) {
    return null;
  }

  return (
    <FloatingBar>
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <span className="fw-bold">
          {t('performanceReview.goals2.bulkApproval.selected', {
            count: selectedGoalIds.size,
          })}
        </span>
        {showRejectComment && (
          <Form.Control
            as="textarea"
            rows={1}
            style={{ maxWidth: '18.75rem' }}
            placeholder={t(i18p + 'rejectCommentPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        )}
        <div className="d-flex gap-2">
          <Button
            variant="success"
            size="sm"
            isProcessing={loadingAction === 'approve'}
            disabled={loadingAction !== null}
            label={t(i18p + 'bulkApprove')}
            onClick={handleApprove}
          />
          <Button
            variant="danger"
            size="sm"
            isProcessing={loadingAction === 'reject'}
            disabled={loadingAction !== null}
            label={t(i18p + 'bulkReject')}
            onClick={handleReject}
          />
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={loadingAction !== null}
            label={t(i18p + 'cancel')}
            onClick={handleCancel}
          />
        </div>
      </div>
    </FloatingBar>
  );
};

const FloatingBar = styled('div')({
  position: 'fixed',
  bottom: '1.5rem',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: Theme.color.white,
  borderRadius: '0.5rem',
  padding: '0.75rem 1.25rem',
  boxShadow: '0 0.25rem 0.75rem rgba(0, 0, 0, 0.15)',
  zIndex: 1050,
  border: `1px solid ${Theme.color.paper3}`,
});
